/**
 * The two blocked states: no organization, and an expired subscription.
 *
 * A port of `bimgo-ai-web`'s two full-screen blocks at the top of
 * `pages/Projects.tsx`. Both are the same card in three tiers — a round icon
 * chip, a title, what happened, and what to do — differing only in icon and
 * tint (grey `Building2`, red `AlertCircle`). The three-tier split is v2's, and
 * it is why each state owns three keys rather than one: they are three elements
 * at three sizes, and one string could only span them by putting markup inside a
 * translation.
 *
 * **It takes the whole window.** Not a pane inside the shell: a blocked user
 * should see this and nothing else. Rendered into the shell's content area it
 * still showed the rail, the nav and the footer of the application it was
 * refusing, which reads as a broken app rather than as an answer. So it renders
 * on the same surface as the login page and the other pre-shell gates — the
 * gradient backdrop and brand lockup — and `App` returns it *instead of*
 * `AppShell`.
 *
 * **The consequence is that it carries its own organization switcher.** Taking
 * the shell away takes the account menu with it, and being blocked on one
 * organization must not strand a user who belongs to another that is perfectly
 * valid. Only organizations with a live subscription are offered, because
 * listing the lapsed ones would just move the user between walls.
 *
 * **No agent status line**, unlike the other pre-shell gates. Those report it
 * because the thing blocking the user *is* the agent, or might be — here it is
 * a subscription, and whether a local process is reachable has no bearing on
 * that and nothing the reader could act on (owner).
 *
 * Departures from v2's text, all deliberate:
 *
 *   - The key `subscriptionExpiredContactAdmin` is renamed `...ContactBimgo`.
 *     Its text says to contact **Bimgo**, not an administrator — an expired
 *     subscription is not something an org admin can fix, which is exactly why
 *     it never reused the shared `contactAdmin` key. Key names are not
 *     user-visible; the text is untouched.
 *   - `subscriptionExpiredDesc` says the **application** is unavailable, where
 *     v2 says project access is. v2 gated one page and could name it; this gates
 *     the product, and telling a user they cannot reach projects while the whole
 *     app is shut would describe a smaller problem than the one they have. It
 *     also drops v2's leading "your organization has no valid subscription"
 *     clause, which the title directly above it already says.
 */
import { useAuth } from "../contexts/AuthProvider";
import { useLocale } from "../i18n/LocaleProvider";
import { hasAnotherValidOrganization, type GateOrganization, type OrgGate } from "../lib/orgGate";

/** lucide `Building2` — v2's glyph for "you are in no organization". */
function BuildingIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
    </svg>
  );
}

/** lucide `AlertCircle` — v2's glyph for the lapsed subscription. */
function AlertCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </svg>
  );
}

export function OrgGateView({
  gate,
  organizations,
  currentOrgId,
  onSelectOrg,
}: {
  gate: Exclude<OrgGate, { kind: "ok" }>;
  organizations: readonly GateOrganization[];
  currentOrgId: string | null;
  onSelectOrg: (id: string) => void;
}) {
  const { t } = useLocale();
  const { signOut } = useAuth();

  const expired = gate.kind === "subscription-expired";
  // Only the ones that would actually let the user in. Offering a lapsed
  // organization moves them from this wall to the same wall.
  const switchable = organizations.filter(
    (o) => o.id !== currentOrgId && o.active_subscription !== null && o.active_subscription !== undefined,
  );
  const canSwitch = expired && hasAnotherValidOrganization(organizations, currentOrgId);

  return (
    <main className="auth-page">
      <div className="auth-shell">
        <div className="auth-header">
          <img className="auth-logo" src="/bimgo-logo-horizontal-color.png" alt="Bimgo AI" />
        </div>
        {/* `role="alert"` on the expired card only: it reports a change of state
            the user did not ask for. Belonging to no organization is the
            ordinary state of a brand-new account, and announcing that as an
            alert would greet every first sign-in with an error. */}
        <div className="auth-card org-gate" {...(expired ? { role: "alert" } : {})}>
          <div className={`org-gate-icon ${expired ? "is-expired" : ""}`}>
            {expired ? <AlertCircleIcon /> : <BuildingIcon />}
          </div>
          <h1 className="org-gate-title">
            {t(expired ? "projects.subscriptionExpired" : "projects.noOrganization")}
          </h1>
          <p className="org-gate-desc">
            {t(expired ? "projects.subscriptionExpiredDesc" : "projects.noOrganizationDesc")}
          </p>
          <p className="org-gate-remedy">
            {t(expired ? "projects.subscriptionExpiredContactBimgo" : "projects.contactAdmin")}
          </p>

          {canSwitch ? (
            <div className="org-gate-switch">
              <div className="org-gate-switch-label">{t("projects.subscriptionExpiredSwitchOrg")}</div>
              {switchable.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="org-gate-org"
                  data-action="switch-org"
                  onClick={() => onSelectOrg(o.id)}
                >
                  {o.name}
                </button>
              ))}
            </div>
          ) : null}

          {/* v2 ends on "contact Bimgo" and nothing else, which is survivable in
              a browser tab — there is a URL bar and there are other tabs. In a
              desktop app it is a locked door, so there is always a way out. */}
          <button
            type="button"
            className="org-gate-action"
            data-action="back-to-login"
            onClick={() => void signOut()}
          >
            {t("projects.backToLogin")}
          </button>
        </div>
      </div>
    </main>
  );
}
